import { useState } from 'react';
import { FontIcon } from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import {
  PagedDataGrid,
  useFetch,
  type DataGridColumn,
  type ICommandBarItem,
} from '@moko/ui';
import {
  loadTenantCategories,
  type TenantCategory,
} from '../../services/tenantOrderAccessService';

type CategoryRow = TenantCategory & { id: number };
const columns: DataGridColumn<CategoryRow>[] = [
  { key: 'code', name: 'Code', minWidth: 140, width: 220, isSortable: true },
  { key: 'label', name: 'Name', minWidth: 180, width: 320, isSortable: true },
  {
    key: 'isActive',
    name: 'Active',
    minWidth: 80,
    width: 100,
    value: (c) => (c.isActive ? 'Yes' : 'No'),
  },
];
export default function TenantCategories() {
  return <CategoryList />;
}
function CategoryList() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<CategoryRow>();
  const categories = useFetch({
    queryKey: ['global-tenant-categories'],
    queryFn: loadTenantCategories,
  });
  const edit = (item: CategoryRow) => {
    void navigate(`/editTenantCategory?code=${encodeURIComponent(item.code)}`);
  };
  const commands: ICommandBarItem[] = [
    {
      key: 'refresh',
      text: 'Refresh',
      icon: <FontIcon iconName="Refresh" />,
      onClick: () => {
        setSelected(undefined);
        categories.refetch();
      },
    },
    {
      key: 'newCategory',
      text: 'New',
      icon: <FontIcon iconName="Add" />,
      onClick: () => {
        void navigate('/newTenantCategory');
      },
    },
    {
      key: 'editCategory',
      text: 'Edit',
      icon: <FontIcon iconName="Edit" />,
      disabled: !selected || categories.loading || !!categories.error,
      onClick: () => {
        if (selected) edit(selected);
      },
    },
  ];
  return (
    <>
      <p>
        Category definitions apply to all tenants. Assign categories and
        configure form availability on each tenant.
      </p>
      {categories.error && (
        <p role="alert">{categories.error.message} Use Refresh to retry.</p>
      )}
      <PagedDataGrid<CategoryRow>
        columns={columns}
        items={(categories.data ?? []).map((c, i) => ({ ...c, id: i + 1 }))}
        loading={categories.loading}
        commandBarItems={commands}
        selectionMode="single"
        selectedId={selected?.id ?? null}
        onSelectionChange={setSelected}
        onRowDoubleClicked={edit}
        empty={{
          title: 'No categories yet!',
          action: {
            text: 'Add category',
            onClick: () => {
              void navigate('/newTenantCategory');
            },
          },
        }}
      />
    </>
  );
}
